import { Component, HostBinding, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FilePreviewComponent } from '@tecalisdev/infinity';
import { CONNECTION_TYPE, IOperationDetailResponse, OPERATION_TYPE } from '../../models/operations.models';
import { ViewMediaService } from '../../../shared/services/view-media.service';
import { OperationsHelperService } from '../../services/operations-helper.service';
import {
	IOperationDocumentsDetail,
	IOperationEvaluationDetail,
	IOperationOcrDetail,
} from '../../../processes/models/processes.models';
import {
	firstOcrBackPreviewUrl,
	firstOcrFrontPreviewUrl,
} from '../../../processes/utils/ocr-admin-images.util';
import { MasterService } from '../../../shared/services/master.service';
import { notAvailableLabel } from '../../../core/utils/utils';

/** Ítem para `tec-file-preview` (URL + pie opcional al estilo Figma). */
export interface OperationDetailFilePreviewItem {
	url: string;
	label: string;
	sizeSm: boolean;
}

@Component({
	selector: 'app-operation-detail-media-preview',
	imports: [FilePreviewComponent],
	templateUrl: './operation-detail-media-preview.component.html',
	styleUrl: './operation-detail-media-preview.component.scss',
})
export class OperationDetailMediaPreviewComponent implements OnChanges {
	@Input() detail: IOperationDetailResponse;

	private viewMedia = inject(ViewMediaService);
	private operationsHelper = inject(OperationsHelperService);
	private trans = inject(TranslateService);
	private masterService = inject(MasterService);

	public previewItems: OperationDetailFilePreviewItem[] = [];

	@HostBinding('class.preview--single-financial')
	public isSingleFinancialDocumentLayout = false;

	public ngOnChanges(changes: SimpleChanges): void {
		if (!changes['detail']) return;
		this.resolvePreviewItems();
	}

	public showViewMedia(selectedUrl: string): void {
		if (!this.detail) return;

		if (
			this.detail.operation_type_name === OPERATION_TYPE.CONNECTIONS ||
			this.detail.operation_type_name === OPERATION_TYPE.FINANCIAL_EVALUATION ||
			this.detail.operation_type_name === OPERATION_TYPE.EVALUATION
		) {
			const payload = this.operationsHelper.buildViewMediaForOperationDetailCard(this.detail);
			if (!payload) {
				return;
			}
			const hasSelected = payload.data.some((media) => media.doc === selectedUrl);
			if (hasSelected) {
				payload.data = payload.data.map((media) => ({
					...media,
					selected: media.doc === selectedUrl,
				}));
			}
			this.viewMedia.setViewMedia(payload);
			return;
		}

		if (this.detail.operation_type_name === OPERATION_TYPE.POL) {
			this.viewMedia.setViewMedia(this.operationsHelper.buildViewMediaSelfieGallery(this.detail, selectedUrl));
			return;
		}

		if (
			this.detail.operation_type_name === OPERATION_TYPE.OCR ||
			this.detail.operation_type_name === OPERATION_TYPE.DOCUMENTS
		) {
			const payload = this.operationsHelper.buildViewMediaForOperationDetailCard(this.detail);
			if (!payload) {
				return;
			}
			const hasSelected = payload.data.some((media) => media.doc === selectedUrl);
			if (hasSelected) {
				payload.data = payload.data.map((media) => ({
					...media,
					selected: media.doc === selectedUrl,
				}));
			}
			this.viewMedia.setViewMedia(payload);
		}
	}

	private resolvePreviewItems(): void {
		this.isSingleFinancialDocumentLayout = false;
		if (!this.detail?.data) {
			this.previewItems = [];
			return;
		}

		const op = this.detail.operation_type_name;

		if (
			op === OPERATION_TYPE.CONNECTIONS ||
			op === OPERATION_TYPE.FINANCIAL_EVALUATION ||
			op === OPERATION_TYPE.EVALUATION
		) {
			const evaluation = this.detail.data as unknown as IOperationEvaluationDetail;
			const docs = (evaluation?.document ?? []).filter(
				(d) => d?.url && String(d.url).trim().length > 0,
			);
			if (!docs.length) {
				this.previewItems = [];
				return;
			}
			const typeRaw = evaluation?.type ?? '';
			const typeKey = typeRaw ? this.financialEvaluationTranslationKey(String(typeRaw)) : '';
			const typeLabel =
				typeKey && this.masterService.hasTranslation(typeKey)
					? this.trans.instant(typeKey)
					: String(typeRaw || notAvailableLabel(this.trans));
			const multi = docs.length > 1;
			this.isSingleFinancialDocumentLayout = !multi;
			this.previewItems = docs.map((d, i) => ({
				url: String(d.url).trim(),
				label: multi ? `${typeLabel} (${i + 1})` : typeLabel,
				sizeSm: multi,
			}));
			return;
		}

		if (op === OPERATION_TYPE.POL) {
			const urls = (this.detail.data.images?.selfie ?? [])
				.slice(0, 2)
				.filter((u): u is string => Boolean(u));
			this.previewItems = urls.map((url) => ({ url, label: '', sizeSm: true }));
			return;
		}

		if (op === OPERATION_TYPE.OCR) {
			const ocr = this.detail.data as unknown as IOperationOcrDetail;
			const imgs = ocr?.images;
			if (!imgs) {
				this.previewItems = [];
				return;
			}
			const urls = [firstOcrFrontPreviewUrl(imgs), firstOcrBackPreviewUrl(imgs)].filter(
				(url): url is string => Boolean(url),
			);
			const unique = Array.from(new Set(urls)).slice(0, 2);
			this.previewItems = unique.map((url) => ({ url, label: '', sizeSm: true }));
			return;
		}

		if (op === OPERATION_TYPE.DOCUMENTS) {
			const docsPayload = this.detail.data as unknown as IOperationDocumentsDetail;
			const urls = (docsPayload?.images?.document ?? [])
				.filter((u): u is string => Boolean(u))
				.slice(0, 2);
			this.previewItems = urls.map((url) => ({ url, label: '', sizeSm: true }));
			return;
		}

		this.previewItems = [];
	}

	/** Alineado con etiquetas `FINANCIAL_EVALUATION.*` (variantes tipo WORK_LIFE vs WORKLIFE). */
	private financialEvaluationTranslationKey(rawType: string): string {
		const t = rawType.trim().toUpperCase();
		const normalized =
			t === 'WORK_LIFE' ? CONNECTION_TYPE.WORK_LIFE : rawType.trim();
		return `FINANCIAL_EVALUATION.${normalized}`;
	}
}
